import * as React from "react";

import {
    FormSelect,
    FormButtonGroup,
    FormButton,
    FormButtonType,
} from "@impero-com/common/components/legacyWidgets";
import {
    getDefaultStartDate,
    getDefaultEndDate,
} from "@impero-com/common/utils/date";
import { formatDateFast } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { set, assertNever } from "@impero-com/common/utils/obj";
import { DatePicker, Popover } from "antd";
import { cx } from "class-variance-authority";
import { range } from "lodash-es";
import moment from "moment-timezone";

import { ModernTabs } from "components/legacyTabs";
import { comparePeriodDates, formatPeriodDate } from "utils/controlResult";
import { formatPeriodBoundary, formatMonth } from "utils/formatting";

import {
    getDefaultPeriodEndDate,
    getDefaultPeriodStartDate,
    toPeriodDate,
    toEndDate,
    toStartDate,
} from "./configuration";
import * as theme from "./theme.css";
import { DateFilter } from "./types";

class DateFilterSelector extends React.Component<
    DateFilterSelectorProps,
    DateFilterSelectorState
> {
    private static readonly TAB_INDEX_DUE_DATE = 0;
    private static readonly TAB_INDEX_PERIOD = 1;
    constructor(props: DateFilterSelectorProps) {
        super(props);
        this.state = {
            dateFilter: props.dateFilter,
            visible: false,
        };
    }
    render() {
        const { isLoading, dateFilter: propsDateFilter } = this.props;
        const { dateFilter: stateDateFilter, visible } = this.state;
        // While the popover is open show the in-progress selection; otherwise props.
        const dateFilter = visible ? stateDateFilter : propsDateFilter;
        let trigger: React.ReactNode;
        let tabIndex: number;
        let dateRangePicker: React.ReactNode = null;
        let periodRangePicker: React.ReactNode = null;
        let validInterval: boolean;
        switch (dateFilter.type) {
            case "DUE_DATE_FILTER": {
                const start = formatDateFast(dateFilter.startDate.toDate());
                const end = formatDateFast(dateFilter.endDate.toDate());
                trigger = (
                    <div className={theme.dateFilterTriggerInner}>
                        <span className={theme.dateFilterTriggerLabel}>
                            {t("Report_Status_DueDate")}
                        </span>{" "}
                        <span className={theme.dateFilterTriggerValue}>
                            {start + "\xa0\u2013\xa0" + end}
                        </span>
                    </div>
                );
                tabIndex = DateFilterSelector.TAB_INDEX_DUE_DATE;
                dateRangePicker = (
                    <div key="due-date" className={theme.dateFilterDueDateTab}>
                        <DatePicker.RangePicker
                            disabled={isLoading}
                            value={[dateFilter.startDate, dateFilter.endDate]}
                            format="DD-MM-YYYY"
                            onChange={(range) => {
                                const startDate = range?.[0];
                                const endDate = range?.[1];
                                if (
                                    this.state.dateFilter.type ===
                                    "DUE_DATE_FILTER"
                                ) {
                                    let newDateFilter = this.state.dateFilter;
                                    if (
                                        startDate !== undefined &&
                                        startDate !== null &&
                                        !newDateFilter.startDate.isSame(
                                            startDate
                                        )
                                    ) {
                                        newDateFilter = set(newDateFilter, {
                                            startDate,
                                        });
                                    }
                                    if (
                                        endDate !== undefined &&
                                        endDate !== null &&
                                        !newDateFilter.endDate.isSame(endDate)
                                    ) {
                                        newDateFilter = set(newDateFilter, {
                                            endDate,
                                        });
                                    }
                                    if (
                                        newDateFilter !== this.state.dateFilter
                                    ) {
                                        this.setState({
                                            dateFilter: newDateFilter,
                                        });
                                    }
                                }
                            }}
                            allowClear={false}
                        />
                    </div>
                );
                validInterval = dateFilter.startDate.isBefore(
                    dateFilter.endDate
                );
                break;
            }
            case "PERIOD_FILTER": {
                const start = formatPeriodBoundary({
                    year: dateFilter.periodStartDate.year,
                    month: dateFilter.periodStartDate.month,
                });
                const end = formatPeriodBoundary({
                    year: dateFilter.periodEndDate.year,
                    month: dateFilter.periodEndDate.month,
                });
                trigger = (
                    <div className={theme.dateFilterTriggerInner}>
                        <span className={theme.dateFilterTriggerLabel}>
                            {t("Report_Status_Period")}
                        </span>{" "}
                        <span className={theme.dateFilterTriggerValue}>
                            {start + "\xa0\u2013\xa0" + end}
                        </span>
                    </div>
                );
                tabIndex = DateFilterSelector.TAB_INDEX_PERIOD;
                const monthOptions = range(1, 13).map((month) => ({
                    label: formatMonth(month),
                    value: month.toString(),
                }));
                const yearOptions = range(2000, 2038).map((year) => ({
                    label: year.toString(),
                    value: year.toString(),
                }));
                periodRangePicker = (
                    <div key="period" className={theme.dateFilterDueDateTab}>
                        <div>
                            <FormSelect
                                id="period-from-month"
                                options={monthOptions}
                                value={dateFilter.periodStartDate.month.toString()}
                                onChange={(monthStr) => {
                                    if (
                                        this.state.dateFilter.type ===
                                        "PERIOD_FILTER"
                                    ) {
                                        const oldStartDate =
                                            this.state.dateFilter
                                                .periodStartDate;
                                        const month = Number(monthStr);
                                        if (month !== oldStartDate.month) {
                                            this.setState({
                                                dateFilter: set(
                                                    this.state.dateFilter,
                                                    {
                                                        periodStartDate:
                                                            toPeriodDate(
                                                                moment([
                                                                    oldStartDate.year,
                                                                    month - 1,
                                                                    1,
                                                                ]).startOf(
                                                                    "month"
                                                                )
                                                            ),
                                                    }
                                                ),
                                            });
                                        }
                                    }
                                }}
                            />
                            <span className={theme.largeBreakingSpace}> </span>
                            <FormSelect
                                id="period-from-year"
                                options={yearOptions}
                                value={dateFilter.periodStartDate.year.toString()}
                                onChange={(yearStr) => {
                                    if (
                                        this.state.dateFilter.type ===
                                        "PERIOD_FILTER"
                                    ) {
                                        const oldStartDate =
                                            this.state.dateFilter
                                                .periodStartDate;
                                        const year = Number(yearStr);
                                        if (year !== oldStartDate.year) {
                                            this.setState({
                                                dateFilter: set(
                                                    this.state.dateFilter,
                                                    {
                                                        periodStartDate:
                                                            toPeriodDate(
                                                                moment([
                                                                    year,
                                                                    oldStartDate.month -
                                                                        1,
                                                                    1,
                                                                ]).startOf(
                                                                    "month"
                                                                )
                                                            ),
                                                    }
                                                ),
                                            });
                                        }
                                    }
                                }}
                            />
                        </div>
                        <div className={theme.dateFilterPeriodTo}>
                            {t("Report_DataSheet_Interval_To")}
                        </div>
                        <div>
                            <FormSelect
                                id="period-to-month"
                                options={monthOptions}
                                value={dateFilter.periodEndDate.month.toString()}
                                onChange={(monthStr) => {
                                    if (
                                        this.state.dateFilter.type ===
                                        "PERIOD_FILTER"
                                    ) {
                                        const oldEndDate =
                                            this.state.dateFilter.periodEndDate;
                                        const month = Number(monthStr);
                                        if (month !== oldEndDate.month) {
                                            this.setState({
                                                dateFilter: set(
                                                    this.state.dateFilter,
                                                    {
                                                        periodEndDate:
                                                            toPeriodDate(
                                                                moment([
                                                                    oldEndDate.year,
                                                                    month - 1,
                                                                    1,
                                                                ]).endOf(
                                                                    "month"
                                                                )
                                                            ),
                                                    }
                                                ),
                                            });
                                        }
                                    }
                                }}
                            />
                            <span className={theme.largeBreakingSpace}> </span>
                            <FormSelect
                                id="period-to-year"
                                options={yearOptions}
                                value={dateFilter.periodEndDate.year.toString()}
                                onChange={(yearStr) => {
                                    if (
                                        this.state.dateFilter.type ===
                                        "PERIOD_FILTER"
                                    ) {
                                        const oldEndDate =
                                            this.state.dateFilter.periodEndDate;
                                        const year = Number(yearStr);
                                        if (year !== oldEndDate.year) {
                                            this.setState({
                                                dateFilter: set(
                                                    this.state.dateFilter,
                                                    {
                                                        periodEndDate:
                                                            toPeriodDate(
                                                                moment([
                                                                    year,
                                                                    oldEndDate.month -
                                                                        1,
                                                                    1,
                                                                ]).endOf(
                                                                    "month"
                                                                )
                                                            ),
                                                    }
                                                ),
                                            });
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                );
                validInterval =
                    comparePeriodDates(
                        dateFilter.periodStartDate,
                        dateFilter.periodEndDate
                    ) <= 0;
                break;
            }
            default:
                return assertNever(dateFilter);
        }
        const tabs = [
            {
                label: t("Report_Status_DueDate"),
                getData: () => dateRangePicker,
            },
        ];
        if (this.props.showPeriodTab) {
            tabs.push({
                label: t("Report_Status_Period"),
                getData: () => periodRangePicker,
            });
        }
        const content = (
            <div className={theme.dateFilterContent}>
                <ModernTabs
                    tabs={tabs}
                    tabIndex={tabIndex}
                    onChange={(tabIndex) => {
                        switch (tabIndex) {
                            case DateFilterSelector.TAB_INDEX_DUE_DATE: {
                                const endDate = getDefaultEndDate();
                                const startDate = getDefaultStartDate(endDate);
                                this.setState({
                                    dateFilter: {
                                        type: "DUE_DATE_FILTER",
                                        startDate,
                                        endDate,
                                    },
                                });
                                break;
                            }
                            case DateFilterSelector.TAB_INDEX_PERIOD: {
                                const periodEndDate = getDefaultPeriodEndDate();
                                const periodStartDate =
                                    getDefaultPeriodStartDate(periodEndDate);
                                this.setState({
                                    dateFilter: {
                                        type: "PERIOD_FILTER",
                                        periodStartDate,
                                        periodEndDate,
                                    },
                                });
                                break;
                            }
                        }
                    }}
                />
                <div className={theme.dateFilterButtonContainer}>
                    <FormButtonGroup>
                        <FormButton
                            id="date-ok-button"
                            type={FormButtonType.Primary}
                            disabled={!validInterval}
                            onClick={() => {
                                this.setState({ visible: false }, () => {
                                    this.props.onChangeDateFilter?.(
                                        this.state.dateFilter
                                    );
                                });
                            }}
                            label={t("Confirm")}
                        />
                        <FormButton
                            id="date-cancel-button"
                            type={FormButtonType.Secondary}
                            onClick={() => {
                                this.setState({ visible: false });
                            }}
                            label={t("Cancel")}
                        />
                    </FormButtonGroup>
                </div>
            </div>
        );
        return (
            <Popover
                trigger="click"
                placement="bottomRight"
                visible={visible}
                content={content}
                onVisibleChange={(nextVisible) => {
                    if (this.props.readonly) {
                        return;
                    }
                    // Only honor opening; OK/Cancel close it, so the popover
                    // survives clicks into the date-picker popup.
                    if (nextVisible) {
                        this.setState({
                            visible: true,
                            dateFilter: this.props.dateFilter,
                        });
                    }
                }}
            >
                <div
                    className={cx(theme.dateFilterTrigger, {
                        [theme.dateFilterTriggerReadOnly]: this.props.readonly,
                    })}
                >
                    {trigger}
                </div>
            </Popover>
        );
    }
}

interface DateFilterSelectorProps {
    isLoading: boolean;
    dateFilter: DateFilter;
    onChangeDateFilter: ((dateFilter: DateFilter) => void) | undefined;
    readonly?: boolean;
    showPeriodTab?: boolean;
}

interface DateFilterSelectorState {
    dateFilter: DateFilter;
    visible: boolean;
}

function setDateFilter(dateFilter: DateFilter) {
    const newUrl = new URL(window.location.href);
    const { searchParams } = newUrl;
    switch (dateFilter.type) {
        case "DUE_DATE_FILTER":
            searchParams.delete("periodStartDate");
            searchParams.delete("periodEndDate");
            searchParams.set(
                "startDate",
                toStartDate(dateFilter.startDate).format()
            );
            searchParams.set("endDate", toEndDate(dateFilter.endDate).format());
            break;
        case "PERIOD_FILTER":
            searchParams.delete("startDate");
            searchParams.delete("endDate");
            searchParams.set(
                "periodStartDate",
                formatPeriodDate(dateFilter.periodStartDate)
            );
            searchParams.set(
                "periodEndDate",
                formatPeriodDate(dateFilter.periodEndDate)
            );
            break;
        default:
            assertNever(dateFilter);
    }

    return newUrl;
}

export { DateFilterSelector, setDateFilter };
